import { pgMonitorPresentation } from '../utils/pgMonitorPresentation';
import { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Info, ChevronRight, X } from 'lucide-react';
import { PgMonitorContext, PgMonitorPanel } from './PgSettlementMonitor';

function DetailDrawer({ onClose }) {
  const panel = useRef(null);
  const closeButton = useRef(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    closeButton.current?.focus();
    const keydown = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); onClose(); }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(panel.current?.querySelectorAll('button:not(:disabled), a[href], [tabindex="0"]') || []);
      const first = focusable[0]; const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.removeEventListener('keydown', keydown); if (previousFocus?.isConnected) previousFocus.focus(); };
  }, [onClose]);
  return createPortal(<div className="fixed inset-0 z-[10000] flex justify-end bg-black/35" onClick={onClose}>
    <aside ref={panel} role="dialog" aria-modal="true" aria-labelledby="pg-monitor-detail-heading" className="flex h-full w-full max-w-[600px] flex-col bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
      <header className="flex shrink-0 items-center justify-between border-b px-5 py-4"><h2 id="pg-monitor-detail-heading" className="text-lg font-extrabold">PG 정산금 검사 상세</h2><button ref={closeButton} type="button" onClick={onClose} aria-label="검사 상세 닫기" className="rounded-lg p-2 hover:bg-slate-100"><X size={20} /></button></header>
      <div className="min-h-0 flex-1 overflow-y-auto p-5"><PgMonitorPanel expanded /><p className="text-xs text-slate-600">사건별 근거와 확인·해소 처리는 검사 이력에서 확인할 수 있습니다.</p></div>
    </aside>
  </div>, document.body);
}

export default function PgMonitorSummary() {
  const monitor = useContext(PgMonitorContext);
  const [open, setOpen] = useState(false);
  // Keep the close handler stable so background polling doesn't reset focus.
  const close = useRef(() => setOpen(false)).current;
  if (!monitor?.active || (monitor.config?.enabled === false && !monitor.error)) return null;
  const { result, error, busy } = monitor;
  const { title, description, warning, className } = pgMonitorPresentation(result, error, busy);
  return <>
    <section aria-label="PG 정산금 검사 요약" className={`mb-3 flex shrink-0 items-center justify-between gap-3 rounded-lg border px-4 py-2.5 ${className}`}>
      <div className="min-w-0"><div className="flex items-center gap-2 text-[13px] font-bold">{warning ? <AlertTriangle aria-hidden="true" size={16} className="shrink-0" /> : <Info aria-hidden="true" size={16} className="shrink-0" />}<h2>PG 정산금 검사 · {title} <span className="ml-2 inline-block rounded-md border border-orange-800 bg-orange-700 px-2.5 py-1 text-[12px] font-extrabold text-white">개발 중</span></h2></div><p role={error ? 'alert' : undefined} className="mt-1 text-[12px] leading-4">{description} <span className="font-bold text-orange-800">개발·검증 중으로 표시 결과가 정확하지 않을 수 있습니다.</span></p></div>
      <button type="button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)} className="flex shrink-0 items-center gap-1 rounded-lg border border-current px-3 py-2 text-[12px] font-bold hover:bg-white/70">상세 보기<ChevronRight aria-hidden="true" size={15} /></button>
    </section>
    {open && <DetailDrawer onClose={close} />}
  </>;
}
